import type { AgentPriority } from '@axilon-apps/domains-ontology-ui-state'

/**
 * Probability-based severity tiers for predictions / confidences. Used to
 * color and label any 0..1 value in the UI consistently across the
 * Analyze page, the run pages, and the config pages.
 *
 * Bands:
 *   healthy   0   ≤ p < 0.10   green
 *   watch     0.10 ≤ p < 0.30  yellow
 *   elevated  0.30 ≤ p < 0.60  orange
 *   critical  0.60 ≤ p ≤ 1     red
 *
 * Mirrors the bands used by `PMConfigDetailPage`'s legend so all four
 * surfaces speak the same color language.
 */
export type SeverityTier = 'healthy' | 'watch' | 'elevated' | 'critical'

/** Returns the tier for a value in `[0, 1]`. Out-of-range / NaN → `healthy`. */
export function severityTier(value: number): SeverityTier {
  if (!Number.isFinite(value) || value < 0) return 'healthy'
  if (value >= 0.6) return 'critical'
  if (value >= 0.3) return 'elevated'
  if (value >= 0.1) return 'watch'
  return 'healthy'
}

/** Lower bound (inclusive) of each tier's probability band, in percent. */
export const TIER_MIN_PCT: Record<SeverityTier, number> = {
  healthy: 0,
  watch: 10,
  elevated: 30,
  critical: 60
}

export const TIER_LABEL: Record<SeverityTier, string> = {
  healthy: 'Healthy',
  watch: 'Watch',
  elevated: 'Elevated',
  critical: 'Critical'
}

export const TIER_BLURB: Record<SeverityTier, string> = {
  healthy: 'Within normal range',
  watch: 'Above baseline',
  elevated: 'Schedule maintenance',
  critical: 'Failure imminent'
}

/**
 * Pill class strings (text + background) keyed on tier. Use as
 * `className={\`rounded-full px-2 py-0.5 \${TIER_PILL_CLASSES[tier]}\`}`.
 */
export const TIER_PILL_CLASSES: Record<SeverityTier, string> = {
  healthy: 'text-st-run bg-st-run-bg',
  watch: 'text-st-warn bg-st-warn-bg',
  elevated: 'text-st-elev bg-st-elev-bg',
  critical: 'text-st-down bg-st-down-bg'
}

/** Text-only tier color (no background). Useful in dense list rows. */
export const TIER_TEXT_CLASSES: Record<SeverityTier, string> = {
  healthy: 'text-st-run',
  watch: 'text-st-warn',
  elevated: 'text-st-elev',
  critical: 'text-st-down'
}

/** Solid bar fill class (used as the foreground inside a track). */
export const TIER_BAR_FILL_CLASSES: Record<SeverityTier, string> = {
  healthy: 'bg-st-run',
  watch: 'bg-st-warn',
  elevated: 'bg-st-elev',
  critical: 'bg-st-down'
}

/** Track class (the unfilled portion under a tier bar). */
export const TIER_BAR_TRACK_CLASSES: Record<SeverityTier, string> = {
  healthy: 'bg-st-run-bg',
  watch: 'bg-st-warn-bg',
  elevated: 'bg-st-elev-bg',
  critical: 'bg-st-down-bg'
}

/** Border class — used for tier-accented card edges, dots, etc. */
export const TIER_BORDER_CLASSES: Record<SeverityTier, string> = {
  healthy: 'border-st-run',
  watch: 'border-st-warn',
  elevated: 'border-st-elev',
  critical: 'border-st-down'
}

/**
 * The action-item priority enum and the chance-derived tier are
 * isomorphic — both describe "how urgent is this". Map between them so
 * `PriorityChip` and prediction pills can share the same color/labels.
 */
export function priorityToTier(priority: AgentPriority): SeverityTier {
  switch (priority) {
    case 'critical':
      return 'critical'
    case 'high':
      return 'elevated'
    case 'medium':
      return 'watch'
    case 'low':
      return 'healthy'
  }
}
